import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, FileText, Briefcase, ArrowRight, Hash, Globe } from 'lucide-react';

// Static pages for search
const SITE_PAGES = [
  { title: 'Home', url: '/', type: 'Page' },
  { title: 'AI & Data Intelligence', url: '/services/ai-data', type: 'Service' },
  { title: 'Cybersecurity', url: '/services/cybersecurity', type: 'Service' },
  { title: 'Application Development', url: '/services/app-development', type: 'Service' },
  { title: 'Cloud & Infrastructure', url: '/services/cloud-infrastructure', type: 'Service' },
  { title: 'Modernization & Automation', url: '/services/modernization', type: 'Service' },
  { title: 'Sustainability', url: '/services/sustainability', type: 'Service' },
  { title: 'About Flynaut', url: '/about', type: 'Page' },
  { title: 'Leadership Team', url: '/about/leadership', type: 'Page' },
  { title: 'Mission & Values', url: '/about/values', type: 'Page' },
  { title: 'Diversity & Inclusion', url: '/about/diversity', type: 'Page' },
  { title: 'Our Brands', url: '/about/brands', type: 'Page' },
  { title: 'Partners', url: '/partners', type: 'Page' },
  { title: 'Industries', url: '/industries', type: 'Page' },
  { title: 'Careers', url: '/careers', type: 'Page' },
  { title: 'Open Positions', url: '/careers/jobs', type: 'Page' },
  { title: 'Life at Flynaut', url: '/careers/life-at-flynaut', type: 'Page' },
  { title: 'Benefits & Perks', url: '/careers/benefits', type: 'Page' },
  { title: 'Internship Program', url: '/careers/internships', type: 'Page' },
  { title: 'Contact Us', url: '/contact', type: 'Page' },
  { title: 'Insights & Blog', url: '/insights', type: 'Page' },
  { title: 'Assessment', url: '/assessment', type: 'Page' },
  { title: 'Engagement Models', url: '/engagement-models', type: 'Page' },
  { title: 'Case Studies', url: '/case-studies', type: 'Page' },
  { title: 'Privacy Policy', url: '/privacy', type: 'Legal' },
  { title: 'Terms of Service', url: '/terms', type: 'Legal' },
  { title: 'Cookie Policy', url: '/cookies', type: 'Legal' },
  { title: 'Accessibility', url: '/accessibility', type: 'Legal' },
];

const TYPE_ICONS = {
  Page: Globe,
  Service: Hash,
  Blog: FileText,
  Job: Briefcase,
  Legal: FileText,
};

export default function SiteSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [blogResults, setBlogResults] = useState([]);
  const [jobResults, setJobResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  // Fetch blogs and jobs for searching
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API}/api/admin/public/blog`).then(r => r.ok ? r.json() : []).then(setBlogResults).catch(() => {});
    fetch(`${API}/api/admin/public/jobs`).then(r => r.ok ? r.json() : []).then(setJobResults).catch(() => {});
  }, [isOpen, API]);

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setResults([]); return; }
    const lq = q.toLowerCase();
    // Static page matches (instant)
    const pageMatches = SITE_PAGES
      .filter(p => p.title.toLowerCase().includes(lq))
      .map(p => ({ ...p, icon: TYPE_ICONS[p.type] || Globe }));
    // Backend full-text search (blog rich content + jobs)
    fetch(`${API}/api/search?q=${encodeURIComponent(q)}&limit=8`)
      .then(r => r.ok ? r.json() : { results: [] })
      .then(data => {
        const backendResults = (data.results || []).map(r => ({
          title: r.title,
          url: r.url,
          type: r.type === 'blog' ? 'Blog' : 'Job',
          icon: r.type === 'blog' ? FileText : Briefcase,
          excerpt: r.excerpt,
        }));
        setResults([...pageMatches, ...backendResults].slice(0, 12));
      })
      .catch(() => {
        // Fallback to client-side blog/job search
        const blogMatches = blogResults
          .filter(p => p.title?.toLowerCase().includes(lq) || p.excerpt?.toLowerCase().includes(lq))
          .slice(0, 5)
          .map(p => ({ title: p.title, url: `/insights/${p.slug}`, type: 'Blog', icon: FileText, excerpt: p.excerpt }));
        const jobMatches = jobResults
          .filter(j => j.title?.toLowerCase().includes(lq) || j.department?.toLowerCase().includes(lq))
          .slice(0, 5)
          .map(j => ({ title: j.title, url: `/careers/jobs/${j.slug}`, type: 'Job', icon: Briefcase, excerpt: `${j.department} - ${j.location}` }));
        setResults([...pageMatches, ...blogMatches, ...jobMatches].slice(0, 12));
      });
  }, [blogResults, jobResults, API]);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 150);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleSelect = (url) => {
    navigate(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="site-search-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-navy-100">
          <Search size={18} className="text-navy-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, articles, jobs..."
            data-testid="site-search-input"
            className="flex-1 text-navy-800 text-base placeholder:text-navy-300 focus:outline-none"
          />
          <button onClick={onClose} className="text-navy-400 hover:text-navy-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {query.trim() && results.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-navy-400 text-sm">No results for "{query}"</p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((r, i) => {
                const Icon = r.icon;
                return (
                  <button
                    key={`${r.url}-${i}`}
                    onClick={() => handleSelect(r.url)}
                    data-testid={`search-result-${i}`}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-navy-50 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-navy-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-navy-800 text-sm font-medium truncate">{r.title}</p>
                      {r.excerpt && <p className="text-navy-400 text-xs truncate mt-0.5">{r.excerpt}</p>}
                    </div>
                    <span className="text-[10px] font-medium text-navy-300 uppercase tracking-wider flex-shrink-0">{r.type}</span>
                  </button>
                );
              })}
            </div>
          )}
          {!query.trim() && (
            <div className="px-5 py-4">
              <p className="text-navy-300 text-xs uppercase tracking-wider mb-3">Quick links</p>
              <div className="flex flex-wrap gap-2">
                {['Services', 'About', 'Careers', 'Contact', 'Insights'].map(q => (
                  <button key={q} onClick={() => setQuery(q)} className="text-xs bg-navy-50 text-navy-600 px-3 py-1.5 rounded-full hover:bg-orange-50 hover:text-orange-600 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-navy-100 flex items-center justify-between text-navy-300 text-[10px]">
          <span>ESC to close</span>
          <span>Search across all pages</span>
        </div>
      </div>
    </div>
  );
}
